import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type DocumentNode, type TypedDocumentNode, type WatchQueryFetchPolicy } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GroupQueryState<TData> = {
  data: TData[];
  total: number;
  loading: boolean;
  /** Offset of the first row in `data` within the total dataset. */
  dataOffset: number;
  /** Jump to a specific offset. The provider re-fetches from that position. */
  fetchPage: (offset: number, limit: number) => void;
  /** Stable token that flips only on filter/sort/search changes. */
  variablesKey: string;
};

type Connection = Omittable<{
  edges: Array<{ node: unknown }>;
  pageInfo: { hasNextPage: boolean };
  total?: number;
}>;

export type UseGroupQueryOptions<TData> = Omittable<{
  /** Unique key for this group. */
  groupKey: string;
  /** GraphQL document to execute (must follow Relay connection shape). */
  document: DocumentNode | TypedDocumentNode;
  /** Query variables (input, first, etc.). */
  variables: Record<string, unknown>;
  /**
   * Extract the Relay connection object from the query result.
   * Example: `(data) => data?.dashboardActivityDrilldown`
   */
  getConnection: (data: any) => Connection | undefined | null;
  /**
   * Report this group's current state to the parent.
   */
  onStateChange: (key: string, state: GroupQueryState<TData>) => void;
  /**
   * Fires when query data arrives. Use to extract sibling connections
   * (e.g. stages, roles, teams) from the same query result.
   */
  onData?: (rawQueryData: any) => void;
  /**
   * Apollo fetch policy. Defaults to "cache-and-network".
   * Use "network-only" for data that must always be fresh (e.g. trash).
   */
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * When false, the group does not subscribe to its query (no watchQuery) and
   * reports an empty, not-loading state. Defaults to true.
   */
  enabled?: boolean;
}>;

/** Stable empty array so a disabled group reports the same reference every render. */
const EMPTY_ROWS: readonly never[] = [];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Runs a paginated query for a single group and reports its state upward
 * via `onStateChange`.
 *
 * Pagination is offset-based: the DataTable calls `fetchPage(offset, limit)`
 * when the virtualizer scrolls to an unloaded region. The hook updates
 * its internal offset override, which changes the query variables and
 * triggers Apollo to re-fetch from that position.
 */
export function useGroupQuery<TData>({
  groupKey,
  document,
  variables,
  getConnection,
  onStateChange,
  onData,
  fetchPolicy = "cache-and-network",
  enabled = true,
}: UseGroupQueryOptions<TData>) {
  const [skipOverride, setSkipOverride] = useState<number>(0);

  // Reset to page 0 when the query context changes (filter/sort/search).
  const variablesKey = JSON.stringify(variables);
  const prevVariablesKeyRef = useRef(variablesKey);

  // Always include skip in variables for consistent cache keys.
  const effectiveVariables = useMemo(() => {
    return { ...variables, skip: skipOverride };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesKey, skipOverride]);

  const client = useApolloClient();
  // Tag queryData with the skip it was fetched for. When skipOverride changes,
  // we must NOT report the previous page's data under the new dataOffset, even
  // for the one render between `setSkipOverride` and the watchQuery effect
  // running. So during render we treat queryData as stale (null) unless its
  // tagged skip matches the current skipOverride.
  const [queryState, setQueryState] = useState<{ data: any; skip: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const fetchedForSkip = (effectiveVariables as { skip?: number }).skip ?? 0;
    const observable = client.watchQuery({
      query: document,
      variables: effectiveVariables,
      fetchPolicy,
    });

    const subscription = observable.subscribe({
      next: (result) => {
        if (result.data !== undefined) {
          setQueryState({ data: result.data, skip: fetchedForSkip });
        }
        setLoading(result.loading);
      },
      error: () => {
        setLoading(false);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [client, document, effectiveVariables, fetchPolicy, enabled]);

  const queryData = queryState && queryState.skip === skipOverride ? queryState.data : null;
  const connection = getConnection(queryData);
  const initialLoading = loading && !queryData && skipOverride === 0;

  // The row IS the connection node — the generated type travels from the query
  // to the leaf component unchanged. Keying on `edges` alone keeps the array
  // reference stable across renders; a fresh array every render would fire the
  // onStateChange effect endlessly and exceed the update depth.
  const mappedData = useMemo(() => {
    if (!connection?.edges) return [];
    return connection.edges.map((edge) => edge.node as TData);
  }, [connection?.edges]);

  // Preserve total across page transitions so the virtualizer keeps its
  // height and scroll position while a new page loads.
  const totalRef = useRef(0);

  // When query context changes (filter/sort/search), reset to page 0
  // and clear the stale total so the virtualizer doesn't over-size.
  if (prevVariablesKeyRef.current !== variablesKey) {
    prevVariablesKeyRef.current = variablesKey;
    if (skipOverride !== 0) setSkipOverride(0);
    totalRef.current = 0;
  }

  if (connection?.total != null) totalRef.current = connection.total;
  const total = totalRef.current;

  const dataOffset = skipOverride;

  // Stable fetchPage — identity never changes.
  const stableFetchPage = useCallback((offset: number, _limit: number) => {
    setSkipOverride(offset);
  }, []);

  // Stable refs to avoid effect re-fires if parent passes new callback identities.
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  // Report sibling data when query result arrives.
  useEffect(() => {
    if (queryData != null) onDataRef.current?.(queryData);
  }, [queryData]);

  // A disabled group reports an explicit empty, not-loading state — otherwise the
  // last-loaded rows linger (mappedData is unchanged, so without `enabled` in the
  // deps this effect wouldn't re-fire on the toggle) and the parent keeps rendering
  // a stale column.
  useEffect(() => {
    onStateChangeRef.current(groupKey, {
      data: enabled ? mappedData : (EMPTY_ROWS as unknown as TData[]),
      total: enabled ? total : 0,
      loading: enabled ? initialLoading : false,
      dataOffset,
      fetchPage: stableFetchPage,
      variablesKey,
    });
  }, [
    enabled,
    groupKey,
    mappedData,
    total,
    initialLoading,
    dataOffset,
    stableFetchPage,
    variablesKey,
  ]);
}
